/* eslint-disable max-lines */

/* Sorting
 *
 * - all alphabetically in related blocks
 * - interfaces
 *   - property order from api documentations
 */

import {
    NinchatAttributes,
    NinchatErrors,
    NinchatEventCauses,
    NinchatEvents,
    NinchatMessages,
    NinchatProperties,
    NinchatSettings,
} from './'
import {BoostHistoryType} from '../../components/BoostHistory/types'
import {HtaConnectorType} from 'components/HtaConnector/types'
import {PrescriptionRenewalSurveyResultsType} from 'components/PrescriptionRenewalSurveyResults/types'

// Types {

// Helper
type Omittable = 'action_id' | 'event' | 'event_id' | 'frames'
// Local
type IncrementID = number
type Url = string
// Base
export type ObjectID = string
// Public
export type AccessHpa = boolean
export type AccessKey = string
export type AccessType = 'session' | 'channel' | 'identity_verify' | 'identity_auth_reset'
export type AccessUrl = Url
export type AccessUrlParams = string
export type ActionID = IncrementID
export type Apihost = string
export type AudienceID = ObjectID
export type CallerIdentityType = 'email'
export type ChannelID = ObjectID
export type ChannelMetadataNote = Pick<NinchatQueueNote, 'label' | 'name' | 'value'>
export type ChannelName = string
export type ChannelStatus = 'unread' | 'highlight'
export type Chat = {channel_id: ChannelID; user_id?: never} | {user_id: UserID; channel_id?: never}
export type ChatID = ChannelID | UserID
export type CommentChange = 'assigned' | 'unassigned' | 'done' // https://github.com/somia/nin/issues/2561#issuecomment-1857101164
export type CommentID = ObjectID
export type CommentText = string
export type ConnActive = number
export type ConnState = ConnStates
export type Count = number
export type Delegate = {
    Name: string
    'Personal identity code': string
}
export type DialogueID = ObjectID
export type DialogueStatus = 'highlight' | 'unread' | 'hidden' | 'visible'
export type Error = string
export type ErrorReason = string
export type ErrorType = `${NinchatErrors.NinchatErrorType}`
export type EventCause = `${NinchatEventCauses.NinchatEventCause}`
export type EventID = IncrementID
export type EventType = `${NinchatEvents.NinchatEventType}`
export type FileID = ObjectID
export type FileName = string
export type FileSize = number
export type FileType = string
export type FileUrl = Url
export type FilterProperty = string
export type FilterSubstring = string
export type Hex = string
export type Highlight = string
export type HistoryOrder = -1 | 1
export type HttpHeaderKey = string
export type HttpHeaderValue = string
export type Iconurl = Url
export type IdentityAuth = string
export type IdentityName = string
export type IdentityType = 'email' | 'gcm' | 'apns'
export type InfoCompany = string
export type InfoJobTitle = string
export type InfoUrl = Url
export type JSON = unknown
export type JitsiRoom = string
export type JitsiToken = string
export type LogMessage = string
export type MasterKeyAttrs = {
    description?: string
    last_use?: Time
    scope?: string[]
}
export type MasterKeyData = {
    master_key_attrs?: MasterKeyAttrs
    realm_id?: RealmID
}
export type MasterKeyID = string
export type MasterKeySecret = string
export type MasterKeyType = 'ninchat' | 'jwt'
export type MasterSign = string
export type MessageID = ObjectID
export type MetadataValue = {[key: string]: MetadataValue} | string
export type Metadata =
    {post_answers?: QuestionnaireAnswers} &
    {pre_answers?: QuestionnaireAnswers} &
    {sso?: Sso} &
    {transfer_comment?: string} &
    {secure?: MetadataValue} &
    {[key: string]: MetadataValue}
export type Metatype = 'geoip'
export type Notifications = Record<string, boolean>
export type PayloadDataFrame = string
export type Pixels = number
export type PlanID = string
export type QuestionnaireAnswers = {[key: string]: boolean | number | string}
export type QueueActivityVersion = 1
export type QueueEventType = string
export type QueueMetadata = Array<{
    agent_id: string,
    audience_id: string,
    channel_id: string,
    complete_time: number,
    metadata: NinchatAudienceMetadata
}>
export type QueueID = ObjectID
export type QueueName = string
export type Ratelimit = string
export type Rating = -1 | 0 | 1
export type RealmID = ObjectID
export type RealmName = string
export type Seconds = number
export type SessionHost = string
export type SessionID = string
export type SiteId = string
export type SiteSecret = string
export type SsoAddress = {
    Katuosoite: string,
    Postinumero: string,
    Postitoimipaikka: string,
}
export type Sso = {
    Authentication: 'canceled' | 'declined' | 'failed' | 'in progress' | 'requested' | 'successful'
    Delegate?: Delegate[]
    Delegation?: 'requested'
    'First name'?: string
    'Last name'?: string
    Mail?: string
    'Personal identity code'?: string
    Test?: boolean
    'Vakinainen kotimainen lähiosoite'?: SsoAddress
}
export type SsoName = string
export type SsoType = string
export type SsoUrl = Url
export type SuggestionID = ObjectID
export type TagID = ObjectID
export type TagName = string
export type ThumbnailUrl = Url
export type ConvertedUrl = Url
export type Time = number
export type Topic = string
export type TranscriptStatus = 'done' // https://github.com/somia/nin/issues/2561#issuecomment-1857101164
export type UserAuth = string
export type UserID = ObjectID
export type UserName = string
export type UserRealname = string
export type UserTranscript = {
    assign_user_id?: UserID
    audience_id: AudienceID
    complete_time: Time
    queue_id: QueueID
    realm_id: RealmID
}
export type UserAudienceTranscripts = {[key: string]: UserTranscript}

// }

// Enums {

export enum ConnStates {
    CONNECTING = 'connecting',
    CONNECTED = 'connected',
    DISCONNECTED = 'disconnected',
}

export enum MessageStatus {
    PENDING = 'pending',
    FULFILLED = 'fulfilled',
    REJECTED = 'rejected',
}

// }

// Interfaces {

// Custom {

export interface Identity {
    name: IdentityName,
    type: IdentityType,
    attrs: NinchatAttributes.NinchatIdentityAttributes
}

// }

// Action referencing {

export interface NinchatMessageAnswer {
    message_id: MessageID
    suggestion_id: SuggestionID
}

// }

// Attribute referencing {

export type NinchatScheduleDay = (string | null)[] | null
export type NinchatScheduleWeek = NinchatScheduleDay[]

export interface NinchatSchedule {
    disabled?: boolean
    timezone: string
    week: NinchatScheduleWeek
    annual?: Record<string, NinchatScheduleDay>
    exceptions?: Record<string, NinchatScheduleDay>
}

// }

// Event referencing {

export interface NinchatAudienceMembers {
    [key: UserID]: {
        agent?: boolean,
        customer?: boolean,
    }
}

export interface SecureMetadata {
    [key: string]: unknown
    _Prescription_renewal_survey_results?: PrescriptionRenewalSurveyResultsType
    shared?: {
        data: Record<string, unknown>
        parent: string[]
    }
}

export interface NinchatAudienceMetadata {
    [key: string]: any
    _Boost_history?: BoostHistoryType
    _HTA_connector?: HtaConnectorType
    _HTIA_connector?: HtaConnectorType
    ninchat?: Record<string, string>
    pre_answers?: Record<string, string | boolean>
    post_answers?: Record<string, string | boolean>
    rating?: Rating
    secure?: SecureMetadata
    sso?: Sso
    tag_ids?: TagID[]
    vars?: Record<string, string>
}

export interface NinchatQueueRequest {
    audience_id: AudienceID
    audience_metadata: NinchatAudienceMetadata | null
    request_time: Time
}

export type NinchatChannelMemberMetadata = NinchatAudienceMetadata

export interface NinchatAudienceRangeComments {
    [key: AudienceID]: NinchatAudienceComment[]
}

export interface NinchatChannelMembers {
    [key: UserID]: NinchatChannelMember
}

export interface NinchatChannelMembersMetadata {
    [key: UserID]: NinchatAudienceMetadata
}

export interface NinchatChannelMetadata {
    [key: string]: unknown
    Notes?: ChannelMetadataNote[]
    tag_ids?: TagID[]
}

export interface NinchatDialogueMembers {
    [key: UserID]: NinchatAttributes.NinchatDialogueMemberAttributes
}

export type NinchatDialogueMetadata = Metadata

export type NinchatMasterKeys = {
    [key in MasterKeyType]: {
        [key: MasterKeyID]: MasterKeyData
    }
}

export type NinchatMemberMessageMetadataContents = {
    Notes?: ChannelMetadataNote[]
    tag_ids?: TagID[]
} & NinchatAudienceMetadata

export interface NinchatMemberMessageMetadata {
    [key: UserID]: NinchatMemberMessageMetadataContents
}

export interface NinchatQueueActivity {
    audience_id?: AudienceID
    event: QueueEventType
    finish_time?: Time
    queue_attrs?: NinchatAttributes.NinchatQueueAttributes
    queue_id?: QueueID
    request_time?: Time
    time: Time
    user_id?: UserID
}

export interface NinchatQueueMembers {
    [key: UserID]: {
        user_attrs: NinchatAttributes.NinchatUserAttributes,
        member_attrs: Record<string, unknown>,
        puppet_attrs?: NinchatAttributes.NinchatPuppetAttributes,
    }
}

export type NinchatQueueSso = Record<string, {
    hpa?: boolean
    required?: boolean
}>

export type StatsAudience = {
    vars?: Record<string, unknown>
    agent_id?: UserID
    accept_count?: number
    finish_count?: number
    finish_duration_avg?: number
    transfer_count?: number
    transfer_queue_id?: QueueID
    ratings?: {
        '-1'?: number
        '1'?: number
        '0'?: number
    }
    register_count?: number
    tag_ids?: TagID[]
}

export type NinchatQueueStats = {
    [key: string]: {
        accept_delay_avg?: number
        audiences?: StatsAudience[]
        describe_count?: number
        drop_count?: number
        drop_delay_avg?: number
        request_count?: number
    }
}

export interface NinchatQueueTranscript {
    request_time: Time
    accept_time: Time
    finish_time?: Time
    complete_time?: Time
    channel_id?: ChannelID
    customer_id?: UserID
    dialogue_id?: UserID[]
    audience_id?: AudienceID
    agent_id?: UserID
    rating?: Rating
}

export interface NinchatRealmMembers {
    [key: UserID]: NinchatRealmMember
}

export interface NinchatRealmQueues {
    [key: QueueID]: {
        queue_attrs: NinchatAttributes.NinchatQueueAttributes,
        queue_sso?: NinchatQueueSso // internal
        queue_position?: Count,
    }
}

export interface NinchatRealmSites {
    [key: QueueID]: {
        site_attrs: NinchatAttributes.NinchatSiteAttributes
    }
}

export interface NinchatSearchChannels {
    [key: ChannelID]: {
        channel_attrs: NinchatAttributes.NinchatChannelAttributes,
        realm_id: RealmID,
        weight: number,
    }
}

export interface NinchatSearchUsers {
    [key: UserID]: {
        user_attrs: NinchatAttributes.NinchatUserAttributes,
        weight: number,
    }
}

export interface NinchatStunServer {
    urls: string[]
}

export interface NinchatTag {
    tag_attrs?: NinchatAttributes.NinchatTagAttributes,
    tag_children: NinchatTagChildren,
    tag_id?: TagID,
}

export interface NinchatTagChildren {
    [key: TagID]: NinchatTag
}

export interface NinchatTranscriptMessage {
    message_id: MessageID
    message_time: Time
    message_type: NinchatMessages.NinchatMessageType
    message_user_id?: UserID
    message_user_name?: UserName
    message_fold?: boolean
    payload: NinchatMessages.NinchatMessage
}

export interface NinchatTurnServer {
    urls: string[]
    username: string
    credential: string
}

// }

// Message referencing {

export interface NinchatFile {
    file_id: FileID
    file_attrs: NinchatAttributes.NinchatFileAttributes
    file_url: string
    thumbnail_url?: string
    url_expiry: number
    converted_url?: string
}

// }

// Setting referencing {


export type NinchatCannedMessageLanguages = Array<{id: string, name: string, isDefault: boolean}> // undocumented

export interface NinchatCannedMessage {
    keywords: string[]
    message: string
    allowedQueues?: Array<QueueID>
    translations?: {
        [K in NinchatCannedMessageLanguages[number]['id']]?: string
    }
    en?: string
    sv?: string
    withLegacyKey?: boolean
}

export interface NinchatQueueNote {
    element: 'input' | 'textarea'
    label: string
    name: string
    required?: boolean
    pattern: string
    type?: 'checkbox' | 'text'
    value?: string
}

// }

// Singulars {

export interface NinchatAudienceComment { // NinchatAudienceRangeComments
    change?: CommentChange
    id: CommentID
    text?: CommentText
    time: Time
    user_id: UserID
}

export interface NinchatChannelMember {
    user_attrs: NinchatAttributes.NinchatUserAttributes
    member_attrs: NinchatAttributes.NinchatChannelMemberAttributes
    member_metadata?: NinchatChannelMembersMetadata
    puppet_attrs?: NinchatAttributes.NinchatPuppetAttributes
}

export interface NinchatRealmMember {
    user_attrs: NinchatAttributes.NinchatUserAttributes
    member_attrs: NinchatAttributes.NinchatRealmMemberAttributes
    puppet_attrs?: NinchatAttributes.NinchatPuppetAttributes
}

export interface NinchatUserChannel {
    channel_attrs: NinchatAttributes.NinchatChannelAttributes
    channel_members?: NinchatChannelMembers
    channel_members_metadata?: NinchatChannelMembersMetadata
    channel_member_count?: Count
    channel_status?: ChannelStatus
    message_time?: Time
    realm_id?: RealmID
    audience_metadata?: NinchatAudienceMetadata
    audience_files?: {[key: FileID]: Pick<NinchatProperties.NinchatFile, 'file_attrs'>}
    channel_metadata?: NinchatChannelMetadata
    owner_id?: UserID
    seen_message_id?: MessageID
    member_message_metadata?: NinchatMemberMessageMetadata // undocumented
}

export interface NinchatUserDialogue {
    dialogue_members?: NinchatDialogueMembers
    dialogue_status?: DialogueStatus
    message_time?: Time
    audience_metadata?: NinchatAudienceMetadata
    dialogue_metadata?: Record<string, unknown>
}

export interface NinchatUserQueue {
    queue_attrs: NinchatAttributes.NinchatQueueAttributes
    queue_requests?: NinchatQueueRequest[]
    queue_settings: NinchatSettings.NinchatQueueSettings
    queue_members?: NinchatQueueMembers
    queue_sso?: NinchatQueueSso // internal
    queue_position?: Count
    queue_time?: Time
    delivery_time?: Time
    realm_id?: RealmID
}

export interface NinchatRealmQueue {
    queue_attrs: NinchatAttributes.NinchatQueueAttributes
    queue_sso?: NinchatQueueSso // internal
    queue_position?: Count
    queue_time?: Time
    realm_id?: RealmID
}

export type NinchatUserRealm = Omit<NinchatEvents.NinchatRealmFoundEvent, Omittable | 'realm_id'>

// }

// States {

export interface NinchatPuppetMasters {
    [key: UserID]: {
        puppet_attrs: NinchatAttributes.NinchatPuppetAttributes,
    }
}

export interface NinchatUserAccount {
    channels: {
        quota: Count,
        available: Count,
    }
    realms: {
        quota: Count,
        available: Count,
    }
    queues: {
        quota: Count,
        available: Count,
    }
    queue_members: {
        quota: Count,
    }
    uploads: {
        quota: Count,
        available: Count,
    }
    subscriptions?: Record<string, unknown>[]
}

export interface NinchatUserChannels {
    [key: ChannelID]: NinchatUserChannel
}

export interface NinchatUserDialogues {
    [key: UserID]: NinchatUserDialogue
}

export type NinchatUserIdentities = {
    [key in IdentityType]?: {
        [key: IdentityName]: NinchatAttributes.NinchatIdentityAttributes
    }
}

export interface NinchatUserQueues {
    [key: QueueID]: NinchatUserQueue
}

export interface NinchatUserRealms {
    [key: RealmID]: NinchatUserRealm
}

export interface NinchatUserRealmsMember {
    [key: RealmID]: NinchatAttributes.NinchatRealmMemberAttributes
}

export type NinchatUserSso = Record<string, {
    failure?: boolean
    metadata?: boolean
}>

export type NinchatUserTranscripts = UserTranscript[]

export const ADMIN_PERMISSIONS = [
    'view_realm',
    'manage_realm',
    'view_realm_site',
    'manage_realm_site',
    'view_realm_queue',
    'manage_realm_queue',
    'update_queue_closed',
    'view_realm_statistics',
    'view_realm_statistics_agent',
    'view_realm_activity',
    'view_realm_transcript',
    'delete_realm_transcript',
    'view_realm_metadata',
] as const

export type PermissionTypes = typeof ADMIN_PERMISSIONS[number]

export type NinchatPermissionGroups = Array<{
    permission_group_id?: string
    permission_group_attrs: {
      admin?: boolean
      name?: string | null,
      ext_name?: string | null
    }
    permissions: Array<PermissionTypes>
    permission_group_member_ids?: Array<UserID>
  }>


// }

// }

// vim:foldmethod=marker:foldmarker={,}
